// Apolo IDE — Multi-file edits XML parser + apply orchestrator (Sprint v11 E6).
//
// LLM emits an Aider-style block:
//
//   <edits>
//     <edit path="src/App.jsx">
//       <old>const x = 1</old>
//       <new>const x = 2</new>
//     </edit>
//     <edit path="src/utils.js" action="create">
//       <new>export function foo() {}</new>
//     </edit>
//     <edit path="src/dead.js" action="delete" />
//   </edits>
//
// `extractEditsBlocks(text)` returns the parsed batches (a single response
// may contain multiple blocks). `applyEdits(batch, io)` reads each file's
// current content via the IO bridge, writes the new content, and rolls
// back every change if any step fails.

const EDITS_BLOCK_RE = /<edits>([\s\S]*?)<\/edits>/g;
// Match either self-closing `<edit ... />` or paired `<edit ...>body</edit>`.
// Use a tempered greedy token so paths-with-slashes (`src/dead.js`) parse
// correctly. attrs are captured in group 1; body (only for the paired form)
// in group 2.
const EDIT_RE = /<edit\b([^>]*?)(?:\/>|>([\s\S]*?)<\/edit>)/g;
const OLD_RE = /<old>([\s\S]*?)<\/old>/;
const NEW_RE = /<new>([\s\S]*?)<\/new>/;
const ATTR_RE = /(\w+)\s*=\s*"([^"]*)"/g;

function unescapeXml(s) {
  if (typeof s !== 'string') return '';
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

function parseAttrs(blob) {
  const out = {};
  ATTR_RE.lastIndex = 0;
  let m;
  while ((m = ATTR_RE.exec(blob || '')) !== null) {
    out[m[1]] = m[2];
  }
  return out;
}

/**
 * Extract all <edits>...</edits> blocks from a streamed LLM response and
 * return them as a flat list of edit operations annotated with batch index.
 *
 * @param {string} text
 * @returns {Array<{
 *   batch: number,
 *   path: string,
 *   action: 'edit' | 'create' | 'delete',
 *   oldContent: string | null,
 *   newContent: string | null,
 * }>}
 */
export function extractEditsBlocks(text) {
  if (typeof text !== 'string' || !text) return [];
  const out = [];
  EDITS_BLOCK_RE.lastIndex = 0;
  let m;
  let batch = 0;
  while ((m = EDITS_BLOCK_RE.exec(text)) !== null) {
    const inner = m[1];
    EDIT_RE.lastIndex = 0;
    let em;
    while ((em = EDIT_RE.exec(inner)) !== null) {
      const attrsRaw = em[1] || '';
      const body = em[2] || '';
      const attrs = parseAttrs(attrsRaw);
      if (!attrs.path) continue;
      const action = attrs.action === 'create' || attrs.action === 'delete' ? attrs.action : 'edit';
      const oldMatch = body.match(OLD_RE);
      const newMatch = body.match(NEW_RE);
      out.push({
        batch,
        path: attrs.path,
        action,
        oldContent: oldMatch ? unescapeXml(oldMatch[1]) : null,
        newContent: action === 'delete' ? null : (newMatch ? unescapeXml(newMatch[1]) : null),
      });
    }
    batch++;
  }
  return out;
}

/**
 * Validate that every edit has the data its action requires.
 * @param {ReturnType<typeof extractEditsBlocks>} edits
 * @returns {{ ok: true } | { ok: false, error: string, index: number }}
 */
export function validateEdits(edits) {
  if (!Array.isArray(edits) || edits.length === 0) {
    return { ok: false, error: 'no edits', index: -1 };
  }
  for (let i = 0; i < edits.length; i++) {
    const e = edits[i];
    if (!e || typeof e.path !== 'string' || !e.path) {
      return { ok: false, error: `edit ${i}: path required`, index: i };
    }
    if (e.action === 'edit' && (typeof e.oldContent !== 'string' || typeof e.newContent !== 'string')) {
      return { ok: false, error: `edit ${i}: action="edit" needs <old> + <new>`, index: i };
    }
    if (e.action === 'create' && typeof e.newContent !== 'string') {
      return { ok: false, error: `edit ${i}: action="create" needs <new>`, index: i };
    }
  }
  return { ok: true };
}

/**
 * Apply a batch of edits atomically. Reads every target file first to take
 * a rollback snapshot, then writes every change. On any failure, restores
 * the snapshots in reverse order.
 *
 * @param {ReturnType<typeof extractEditsBlocks>} edits
 * @param {{
 *   read: (path: string) => Promise<string|null>,
 *   write: (path: string, content: string) => Promise<void>,
 *   remove?: (path: string) => Promise<void>,
 * }} io
 * @returns {Promise<{ ok: true, applied: number } | { ok: false, error: string, rolledBack: number }>}
 */
export async function applyEdits(edits, io) {
  const valid = validateEdits(edits);
  if (!valid.ok) return { ok: false, error: valid.error, rolledBack: 0 };
  if (typeof io?.read !== 'function' || typeof io?.write !== 'function') {
    return { ok: false, error: 'io.read / io.write required', rolledBack: 0 };
  }

  // Snapshot every affected path so we can restore on failure.
  // `snapshots` keeps the ORIGINAL content (rollback target).
  // `currentByPath` tracks the LIVE content after each accumulated edit so
  // multiple edits to the same file compound correctly (bug found via the
  // self-improvement loop: previous version always replaced against the
  // original snapshot, so only the last edit on each path persisted).
  const snapshots = new Map();
  const currentByPath = new Map();
  for (const e of edits) {
    if (snapshots.has(e.path)) continue;
    try {
      const existing = await io.read(e.path);
      snapshots.set(e.path, existing); // may be null when file is new
      currentByPath.set(e.path, existing ?? '');
    } catch (err) {
      return { ok: false, error: `read snapshot failed for ${e.path}: ${err?.message}`, rolledBack: 0 };
    }
  }

  const applied = [];
  try {
    for (const e of edits) {
      if (e.action === 'delete') {
        if (typeof io.remove === 'function') {
          await io.remove(e.path);
          applied.push(e);
        } else {
          // No remove support — fall back to empty write.
          await io.write(e.path, '');
          applied.push(e);
        }
        currentByPath.set(e.path, '');
        continue;
      }
      if (e.action === 'create') {
        const next = e.newContent ?? '';
        await io.write(e.path, next);
        currentByPath.set(e.path, next);
        applied.push(e);
        continue;
      }
      // edit: verify oldContent matches the CURRENT in-memory state (which
      // includes any earlier edits on this path), not the original snapshot.
      const current = currentByPath.get(e.path) ?? '';
      let next;
      if (e.oldContent && e.oldContent !== current) {
        // Try a fuzzy substring replace as a fallback (matches Aider's UX).
        if (current.includes(e.oldContent)) {
          next = current.replace(e.oldContent, e.newContent ?? '');
        } else {
          throw new Error(`edit oldContent not found in ${e.path}`);
        }
      } else if (e.oldContent === current) {
        // Full-file replace.
        next = e.newContent ?? '';
      } else {
        // No oldContent provided — treat as full-file write.
        next = e.newContent ?? '';
      }
      await io.write(e.path, next);
      currentByPath.set(e.path, next);
      applied.push(e);
    }
    return { ok: true, applied: applied.length };
  } catch (err) {
    // Rollback in reverse order.
    let restored = 0;
    for (let i = applied.length - 1; i >= 0; i--) {
      const e = applied[i];
      const snapshot = snapshots.get(e.path);
      try {
        if (snapshot === null) {
          // File was created in this batch — best-effort delete it.
          if (typeof io.remove === 'function') await io.remove(e.path);
          else await io.write(e.path, '');
        } else {
          await io.write(e.path, snapshot);
        }
        restored++;
      } catch {
        // Continue restoring others
      }
    }
    return { ok: false, error: err?.message || 'apply failed', rolledBack: restored };
  }
}
